import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Card,
  Grid,
  Group,
  Loader,
  Progress,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Badge,
  Button,
  Select,
  TextInput,
} from "@mantine/core";
import {
  IconHeart,
  IconRefresh,
  IconVideo,
  IconCalendarEvent,
  IconUserExclamation,
  IconSearch,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";

const getRepProfileImage = rep =>
  rep.profile_image_url || rep.profile_image || rep.avatar_url || undefined;

const getRepInitials = rep =>
  rep.rep_name
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(name => name.charAt(0).toUpperCase())
    .join("") || "R";

const getProgramCompletionPercent = rep => {
  const totalAssigned = Number(rep.total_programs_assigned) || 0;
  const completed = Number(rep.programs_completed) || 0;

  if (totalAssigned <= 0) return 0;
  return Math.round((completed / totalAssigned) * 100);
};

const getCardFilterLabel = days => {
  switch (Number(days) || 7) {
    case 7:
      return "this week";
    case 14:
      return "the last 2 weeks";
    case 21:
      return "the last 3 weeks";
    case 30:
      return "the last 30 days";
    default:
      return "the selected period";
  }
};

function RepHealthTab() {
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [cardWindowDays, setCardWindowDays] = useState("7");
  const [searchQuery, setSearchQuery] = useState("");

  const parsedCardWindowDays = Number(cardWindowDays) || 7;
  const cardFilterLabel = getCardFilterLabel(parsedCardWindowDays);

  const daysSince = value => {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    if (diffMs < 0) return 0;

    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  const repMetrics = (cardData?.reps || []).reduce(
    (acc, rep) => {
      const trainingMinutes = Number(rep.training_minutes) || 0;
      const daysSinceTeamMeeting = daysSince(rep.last_team_meeting_at);
      const daysSincePersonalMeeting = daysSince(rep.last_manager_meeting_at);
      const assignedTeamMeetings = Number(rep.team_meetings_assigned) || 0;
      const missedTeamMeetings = Number(rep.missed_team_meetings) || 0;

      if (trainingMinutes === 0) {
        acc.noTrainingMinutes += 1;
      }

      if (
        assignedTeamMeetings > 0 &&
        (missedTeamMeetings > 0 ||
          daysSinceTeamMeeting === null ||
          daysSinceTeamMeeting >= parsedCardWindowDays)
      ) {
        acc.noWeeklyTraining += 1;
      }

      if (
        daysSincePersonalMeeting === null ||
        daysSincePersonalMeeting >= parsedCardWindowDays
      ) {
        acc.noPersonalMeeting += 1;
      }

      return acc;
    },
    {
      noTrainingMinutes: 0,
      noWeeklyTraining: 0,
      noPersonalMeeting: 0,
    }
  );

  const filteredReps = (data?.reps || []).filter(rep => {
    if (!searchQuery.trim()) return true;

    return rep.rep_name
      ?.toLowerCase()
      .includes(searchQuery.trim().toLowerCase());
  });

  const fetchRepHealth = async () => {
    try {
      setLoading(true);
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint(`/v1/rep/rep-health?days=${parsedCardWindowDays}`),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load rep health data");
      }

      const result = await response.json();
      setData(result.data);
      setCardData(result.data);
    } catch (error) {
      showError(error.message || "Failed to load rep health data");
    } finally {
      setLoading(false);
    }
  };

  const fetchCardMetrics = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(
        getApiEndpoint(`/v1/rep/rep-health?days=${parsedCardWindowDays}`),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load rep health card metrics");
      }

      const result = await response.json();
      setCardData(result.data);
    } catch (error) {
      showError(error.message || "Failed to load rep health card metrics");
    }
  };

  useEffect(() => {
    fetchRepHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!data) return;
    if (parsedCardWindowDays === 7) return;
    fetchCardMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, parsedCardWindowDays]);

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="lg" />
        <Text>Loading rep health…</Text>
      </Group>
    );
  }

  if (!data) {
    return (
      <Card withBorder radius="md" p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size={72} radius="xl" variant="light" color="gray">
            <IconHeart size={36} />
          </ThemeIcon>
          <Text c="dimmed">No rep health data available yet.</Text>
          <Button variant="outline" onClick={fetchRepHealth}>
            Retry
          </Button>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="lg">
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <Card padding="xl" radius="lg" withBorder h="100%">
            <Stack gap="lg" justify="space-between" h="100%">
              <ThemeIcon size={42} radius="md" variant="light" color="red">
                <IconHeart size={22} />
              </ThemeIcon>
              <div>
                <Text size="sm" c="dimmed" fw={500} mb={4}>
                  Average Rep Health Score
                </Text>
                <Text fz="2xl" fw={700}>
                  {data.summary?.average_score ?? 0}
                </Text>
              </div>
              <Text size="sm" c="dimmed">
                Across scored reps
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <Card padding="xl" radius="lg" withBorder h="100%">
            <Stack gap="lg" justify="space-between" h="100%">
              <ThemeIcon size={42} radius="md" variant="light" color="orange">
                <IconVideo size={22} />
              </ThemeIcon>
              <div>
                <Text size="sm" c="dimmed" fw={500} mb={4}>
                  No Training Minutes
                </Text>
                <Text fz="2xl" fw={700}>
                  {repMetrics.noTrainingMinutes}
                </Text>
              </div>
              <Text size="sm" c="dimmed">
                Reps with No Training within {cardFilterLabel}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <Card padding="xl" radius="lg" withBorder h="100%">
            <Stack gap="lg" justify="space-between" h="100%">
              <ThemeIcon size={42} radius="md" variant="light" color="blue">
                <IconCalendarEvent size={22} />
              </ThemeIcon>
              <div>
                <Text size="sm" c="dimmed" fw={500} mb={4}>
                  Missed Weekly Training
                </Text>
                <Text fz="2xl" fw={700}>
                  {repMetrics.noWeeklyTraining}
                </Text>
              </div>
              <Text size="sm" c="dimmed">
                No team training attendance in {cardFilterLabel}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, lg: 3 }}>
          <Card padding="xl" radius="lg" withBorder h="100%">
            <Stack gap="lg" justify="space-between" h="100%">
              <ThemeIcon size={42} radius="md" variant="light" color="grape">
                <IconUserExclamation size={22} />
              </ThemeIcon>
              <div>
                <Text size="sm" c="dimmed" fw={500} mb={4}>
                  No Personal Meeting
                </Text>
                <Text fz="2xl" fw={700}>
                  {repMetrics.noPersonalMeeting}
                </Text>
              </div>
              <Text size="sm" c="dimmed">
                No personal meeting in {cardFilterLabel}
              </Text>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <Card withBorder radius="md" padding={0} style={{ minHeight: 620 }}>
        <Stack gap="lg">
          <Group justify="space-between" align="center" p="lg" pb="md">
            <Title order={3}>Rep Health</Title>
            <Group gap="sm" align="center" wrap="nowrap">
              <Button
                variant="outline"
                leftSection={<IconRefresh size={16} />}
                onClick={fetchRepHealth}
              >
                Refresh
              </Button>
              <Select
                placeholder="Filter Cards"
                value={cardWindowDays}
                onChange={value => setCardWindowDays(value || "")}
                data={[
                  { value: "7", label: "This week" },
                  { value: "14", label: "Last 2 weeks" },
                  { value: "21", label: "Last 3 weeks" },
                  { value: "30", label: "Last 30 days" },
                ]}
                allowDeselect={false}
                w={180}
              />
              <TextInput
                placeholder="Search reps"
                value={searchQuery}
                onChange={event => setSearchQuery(event.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                w={220}
              />
            </Group>
          </Group>

          <div style={{ overflow: "hidden", minHeight: 540 }}>
            <Table
              highlightOnHover
              verticalSpacing="md"
              horizontalSpacing="lg"
              stickyHeader
            >
              <Table.Thead>
                <Table.Tr>
                  {[
                    "Rep",
                    "Rep. Health",
                    "Video Training",
                    "Program Progess",
                    "Personal Meetings",
                    "Team Trainings",
                    "Pitch Reviews",
                  ].map(header => (
                    <Table.Th
                      key={header}
                      style={{
                        background: "#fafbfc",
                        borderTop: "1px solid #e5e7eb",
                      }}
                    >
                      {header}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredReps.map(rep => (
                  <Table.Tr
                    key={rep.user_season_id}
                    style={{ cursor: rep.rep_id ? "pointer" : "default" }}
                    onClick={() => {
                      if (!rep.rep_id) return;
                      navigate(`/reps/${rep.rep_id}`);
                    }}
                  >
                    <Table.Td>
                      <Group gap="md" wrap="nowrap">
                        <Avatar
                          src={getRepProfileImage(rep)}
                          radius="xl"
                          size="lg"
                          color="orange"
                        >
                          {getRepInitials(rep)}
                        </Avatar>
                        <Text fw={600} size="md">
                          {rep.rep_name}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4} style={{ minWidth: 140 }}>
                        <Group gap="xs">
                          <Badge
                            color={
                              rep.health_score >= 70
                                ? "green"
                                : rep.health_score >= 50
                                  ? "yellow"
                                  : "red"
                            }
                          >
                            {rep.health_label}
                          </Badge>
                          <Text fw={700}>{rep.health_score}</Text>
                        </Group>
                        <Progress
                          value={rep.health_score}
                          color={
                            rep.health_score >= 70
                              ? "green"
                              : rep.health_score >= 50
                                ? "yellow"
                                : "red"
                          }
                        />
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="md">
                          {rep.training_minutes} total minutes
                        </Text>
                        <Text size="sm" c="dimmed">
                          {rep.training_courses_completed} courses completed
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="md">
                          {getProgramCompletionPercent(rep)}% completed
                        </Text>
                        <Text size="sm" c="dimmed">
                          {rep.programs_completed}/
                          {rep.total_programs_assigned || 0} completed
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="md">
                          {daysSince(rep.last_manager_meeting_at) === null
                            ? "—"
                            : `${daysSince(rep.last_manager_meeting_at)} days ago`}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {rep.manager_meetings_attended} attended
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="md">
                          {daysSince(rep.last_team_meeting_at) === null
                            ? "—"
                            : `${daysSince(rep.last_team_meeting_at)} days ago`}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {rep.team_meetings_assigned > 0
                            ? `${rep.team_meetings_attended}/${rep.team_meetings_assigned} attended`
                            : "No assigned trainings"}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="md">
                          {daysSince(rep.last_pitch_review_submitted_at) ===
                          null
                            ? "—"
                            : `${daysSince(rep.last_pitch_review_submitted_at)} days ago`}
                        </Text>
                        <Text size="sm" c="dimmed">
                          Avg score: {rep.pitch_review_average_score ?? "—"} ·{" "}
                          {rep.pitch_reviews_sent} sent
                        </Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {filteredReps.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text ta="center" c="dimmed" py="md">
                        No reps found.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </div>
        </Stack>
      </Card>
    </Stack>
  );
}

export default RepHealthTab;
